import TagInput from "../TagInput";
import DatasetVersionAuthorInput from "../DatasetVersionAuthorInput";
import { Combobox } from "@/kit/ui/combobox";
import { Divider } from "@/kit/ui/divider";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/kit/ui/form";
import { Textarea } from "@/kit/ui/textarea";
import { LICENSES } from "@/utils/license";
import { useValibotFormContext } from "@/utils/validation";
import { FormattedMessage, useIntl } from "react-intl";
import { Schema, SHORT_DESCRIPTION_MAX_SIZE } from "./schema";
import {
  Tooltip,
  TooltipPortal,
  TooltipTrigger,
  TooltipContent,
} from "@/kit/ui/tooltip";
import { InfoCircleIcon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { PropsWithChildren } from "react";

interface FormFieldWithTooltipProps extends PropsWithChildren {
  ariaLabel: string;
  label: React.ReactNode;
  tooltipMessage: React.ReactNode;
}

export function FormFieldWithTooltip({
  ariaLabel,
  label,
  tooltipMessage,
  children,
}: FormFieldWithTooltipProps) {
  return (
    <Tooltip delayDuration={0}>
      <FormItem>
        <FormLabel className="flex flex-row items-center gap-1.5">
          {label}
          <TooltipTrigger asChild>
            <Button icon variant="link" size="xs" aria-label={ariaLabel}>
              <InfoCircleIcon className="h-5 w-5 text-grey-500" />
            </Button>
          </TooltipTrigger>
        </FormLabel>
        {children}
        <TooltipPortal>
          <TooltipContent>{tooltipMessage}</TooltipContent>
        </TooltipPortal>
      </FormItem>
    </Tooltip>
  );
}

export function DatasetVersionEditForm() {
  const intl = useIntl();
  const form = useValibotFormContext<Schema>();

  return (
    <>
      <FormField
        control={form.control}
        name="shortDescription"
        render={({ field: { value, ...field } }) => (
          <FormFieldWithTooltip
            ariaLabel={intl.formatMessage({
              defaultMessage: "More information about short description",
            })}
            label={<FormattedMessage defaultMessage="Short description" />}
            tooltipMessage={
              <FormattedMessage defaultMessage="Describe what your dataset is about in a few words." />
            }
          >
            <FormControl>
              <Textarea
                value={value ?? ""}
                maxLength={SHORT_DESCRIPTION_MAX_SIZE + 1}
                placeholder={intl.formatMessage({
                  defaultMessage: "Add description",
                })}
                onPointerDown={(e) => e.stopPropagation()}
                {...field}
              />
            </FormControl>
            {!form.formState.errors.shortDescription && (
              <FormDescription>
                <FormattedMessage
                  defaultMessage="{count}/{maxChar} characters used"
                  values={{
                    count: form.watch("shortDescription")?.length ?? 0,
                    maxChar: SHORT_DESCRIPTION_MAX_SIZE,
                  }}
                />
              </FormDescription>
            )}
            <FormMessage />
          </FormFieldWithTooltip>
        )}
      />
      <Divider />
      <FormField
        control={form.control}
        name="tags"
        render={({ field }) => (
          <FormFieldWithTooltip
            ariaLabel={intl.formatMessage({
              defaultMessage: "More information about categories",
            })}
            label={<FormattedMessage defaultMessage="Categories" />}
            tooltipMessage={
              <FormattedMessage defaultMessage="Add categories to make your dataset easier to find." />
            }
          >
            <FormControl>
              <TagInput
                activityKind="DATASET"
                placeholder={intl.formatMessage({
                  defaultMessage: "Add categories",
                })}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormFieldWithTooltip>
        )}
      />
      <Divider />
      <FormField
        control={form.control}
        name="license"
        render={({ field }) => (
          <FormFieldWithTooltip
            ariaLabel={intl.formatMessage({
              defaultMessage: "More information about licenses",
            })}
            label={<FormattedMessage defaultMessage="Licenses" />}
            tooltipMessage={
              <FormattedMessage defaultMessage="Let others know how they can use your dataset." />
            }
          >
            <FormControl>
              <Combobox
                items={LICENSES.map((license) => ({
                  value: license,
                  label: license,
                }))}
                emptyMessage={intl.formatMessage({
                  defaultMessage: "No license found",
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Add license",
                })}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormFieldWithTooltip>
        )}
      />
      <Divider />
      <FormField
        control={form.control}
        name="authors"
        render={({ field }) => (
          <FormFieldWithTooltip
            ariaLabel={intl.formatMessage({
              defaultMessage: "More information about authors",
            })}
            label={<FormattedMessage defaultMessage="Authors" />}
            tooltipMessage={
              <FormattedMessage defaultMessage="Be sure to acknowledge the individuals who contributed to this version of the dataset to enhance their exposure." />
            }
          >
            <FormControl>
              <DatasetVersionAuthorInput {...field} />
            </FormControl>

            <FormMessage />
          </FormFieldWithTooltip>
        )}
      />
    </>
  );
}
